<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1400px] mx-auto">

    <!-- ============ HERO ============ -->
    <div class="relative overflow-hidden rounded-3xl bg-neutral-900 text-white px-6 sm:px-8 py-7 mb-6 shadow-lg">
      <div class="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl"></div>
      <div class="pointer-events-none absolute right-10 bottom-0 opacity-[0.07]"><mat-icon class="!text-[160px] !h-[160px] !w-[160px]">local_shipping</mat-icon></div>

      <div class="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div class="max-w-xl">
          <div class="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">flight_takeoff</mat-icon> Delivery
          </div>
          <h1 class="mt-2 text-3xl font-bold tracking-tight">Delivery Partners</h1>
          <p class="mt-2 text-sm text-white/60 leading-relaxed">The couriers and hauliers that take orders out. Staff pick one when they close out an order in Validate Orders.</p>
        </div>
        <button type="button"
          class="group self-start lg:self-auto flex items-center gap-2.5 pl-2.5 pr-5 py-2.5 rounded-2xl bg-amber-400 text-neutral-900 font-bold text-sm shadow-lg shadow-amber-500/20 hover:bg-amber-300 hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-4 focus:ring-amber-400/40"
          (click)="openAdd()">
          <span class="h-8 w-8 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform duration-200 group-hover:rotate-90">
            <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
          </span>
          New partner
        </button>
      </div>

      <div class="relative mt-7 grid grid-cols-3 gap-3 max-w-2xl">
        @for (s of [
          { label: 'Partners', icon: 'local_shipping', n: loading ? undefined : partners.length },
          { label: 'Active', icon: 'toggle_on', n: loading ? undefined : activeCount },
          { label: 'Orders sent', icon: 'inventory_2', n: loading ? undefined : ordersCount }
        ]; track s.label) {
        <div class="rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold text-white/50 uppercase tracking-wide"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ s.icon }}</mat-icon>{{ s.label }}</div>
          <div class="mt-1 text-2xl font-bold tabular-nums">{{ s.n === undefined || s.n === null ? '–' : (s.n | number) }}</div>
        </div>
        }
      </div>
    </div>

    @if (loadError) {
    <div class="mb-4 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <mat-icon class="!text-[20px]">error_outline</mat-icon>
      <span class="flex-1">{{ loadError }}</span>
      <button class="font-semibold underline" (click)="load()">Try again</button>
    </div>
    }

    <!-- ============ LIST ============ -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      @if (loading) {
        @for (i of [1, 2, 3]; track i) {
        <div class="flex items-center gap-4 px-5 py-4 border-b border-gray-50 animate-pulse">
          <div class="h-11 w-11 rounded-xl bg-gray-100"></div>
          <div class="flex-1 space-y-2"><div class="h-3.5 w-48 bg-gray-100 rounded"></div><div class="h-3 w-32 bg-gray-100 rounded"></div></div>
        </div>
        }
      } @else if (!partners.length && !loadError) {
        <div class="m-5 rounded-2xl border-2 border-dashed border-gray-200 px-6 py-12 text-center">
          <mat-icon class="!text-[40px] !h-10 !w-10 text-gray-300">local_shipping</mat-icon>
          <div class="mt-2 font-semibold text-gray-900">No delivery partners yet</div>
          <p class="mt-1 text-sm text-gray-500">Add the couriers you use - staff need at least one to close out orders.</p>
          <button class="mt-4 px-4 py-2 rounded-xl bg-neutral-900 text-amber-400 text-sm font-semibold" (click)="openAdd()">Add a partner</button>
        </div>
      } @else {
        @for (p of partners; track p.id) {
        <div role="button" tabindex="0" (click)="openEdit(p)" (keydown.enter)="openEdit(p)"
          class="group flex items-center gap-4 px-5 py-4 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-amber-50/40 transition-colors"
          [class.opacity-60]="!p.active">
          <div class="h-11 w-11 flex-shrink-0 rounded-xl flex items-center justify-center shadow-sm"
            [ngClass]="p.active ? 'bg-gradient-to-br from-amber-300 to-amber-500 text-neutral-900' : 'bg-gray-100 text-gray-400'">
            <mat-icon>local_shipping</mat-icon>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 min-w-0">
              <span class="font-semibold text-gray-900 truncate text-[15px]">{{ p.name }}</span>
              @if (!p.active) { <span class="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md bg-gray-100 text-gray-500">Off</span> }
            </div>
            <div class="text-[12px] text-gray-500 truncate">
              {{ p.phone || 'No phone' }}@if (p.trackingUrl) { · <span class="text-sky-700">tracking link</span> }
            </div>
          </div>
          <span class="hidden sm:inline-block flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 tabular-nums">{{ p.orders ?? 0 }} order{{ p.orders === 1 ? '' : 's' }}</span>
          <div class="flex items-center gap-1 flex-shrink-0" (click)="$event.stopPropagation()">
            <button class="h-9 w-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100" [matTooltip]="p.active ? 'Switch off' : 'Switch on'" (click)="toggleActive(p)">
              <mat-icon [ngClass]="p.active ? 'text-green-600' : 'text-gray-400'">{{ p.active ? 'toggle_on' : 'toggle_off' }}</mat-icon>
            </button>
            <button class="h-9 w-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-red-50 hover:text-red-600" matTooltip="Delete" (click)="askDelete(p)">
              <mat-icon class="!text-[20px]">delete</mat-icon>
            </button>
          </div>
        </div>
        }
      }
    </div>
  </div>
  </div>

  <!-- ============ ADD / EDIT SLIDE-OVER ============ -->
  @if (editing) {
  <div class="fixed inset-0 z-50 bg-neutral-900/40 pc-fade" (click)="closeForm()"></div>
  <aside class="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col pc-slide">
    <div class="relative overflow-hidden bg-neutral-900 text-white px-6 pt-6 pb-5">
      <button class="absolute right-4 top-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" (click)="closeForm()" aria-label="Close"><mat-icon>close</mat-icon></button>
      <div class="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">{{ editing.id ? 'Edit partner' : 'New partner' }}</div>
      <div class="mt-1 text-xl font-bold truncate pr-10">{{ editing.name || 'Delivery partner' }}</div>
    </div>
    <form class="flex-1 overflow-y-auto px-6 py-5 space-y-4" (ngSubmit)="save()">
      <label class="block">
        <span class="text-xs font-medium text-gray-600">Name *</span>
        <input name="name" [(ngModel)]="editing.name" maxlength="100" required placeholder="e.g. Palletways"
          class="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none" />
      </label>
      <label class="block">
        <span class="text-xs font-medium text-gray-600">Phone</span>
        <input name="phone" [(ngModel)]="editing.phone" maxlength="30" placeholder="Depot or account manager"
          class="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none" />
      </label>
      <label class="block">
        <span class="text-xs font-medium text-gray-600">Tracking link</span>
        <input name="trackingUrl" [(ngModel)]="editing.trackingUrl" maxlength="255" placeholder="https://..."
          class="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none" />
        <span class="mt-1 block text-[11px] text-gray-400">Where staff can look up a consignment.</span>
      </label>
      <label class="flex items-center gap-3 rounded-xl border border-gray-200 px-3 py-2.5 cursor-pointer">
        <input type="checkbox" name="active" [(ngModel)]="editing.active" class="h-4 w-4 accent-amber-500" />
        <span class="text-sm"><span class="font-semibold text-gray-900">Active</span><span class="block text-xs text-gray-500">Only active partners can be picked when closing out an order.</span></span>
      </label>
      @if (formError) { <div class="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{{ formError }}</div> }
      <button type="submit" class="hidden"></button>
    </form>
    <div class="border-t border-gray-100 px-6 py-4 flex items-center gap-2">
      <button class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-400 text-neutral-900 font-bold text-sm hover:bg-amber-300 disabled:opacity-60" [disabled]="saving" (click)="save()">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">save</mat-icon>{{ saving ? 'Saving…' : editing.id ? 'Save changes' : 'Add partner' }}
      </button>
      <button class="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50" (click)="closeForm()">Cancel</button>
      @if (editing.id) {
      <button class="ml-auto h-10 w-10 rounded-xl border border-red-100 text-red-600 hover:bg-red-50 flex items-center justify-center" matTooltip="Delete" (click)="askDelete(editing)"><mat-icon class="!text-[20px]">delete</mat-icon></button>
      }
    </div>
  </aside>
  }

  <!-- ============ DELETE CONFIRM ============ -->
  @if (deleting) {
  <div class="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-900/50 px-4 pc-fade" (click)="deleting = null">
    <div class="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" (click)="$event.stopPropagation()">
      <div class="h-11 w-11 rounded-full bg-red-50 text-red-600 flex items-center justify-center"><mat-icon>delete</mat-icon></div>
      <div class="mt-3 font-bold text-gray-900 text-lg">Delete {{ deleting.name }}?</div>
      <p class="mt-1 text-sm text-gray-600">
        @if (deleting.orders) { It's on {{ deleting.orders }} order{{ deleting.orders === 1 ? '' : 's' }}, so it will be switched off instead and those orders keep it. }
        @else { This can't be undone. }
      </p>
      <div class="mt-5 flex gap-2">
        <button class="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-60" [disabled]="removing" (click)="confirmDelete()">{{ removing ? 'Working…' : deleting.orders ? 'Switch off' : 'Delete' }}</button>
        <button class="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700" (click)="deleting = null">Cancel</button>
      </div>
    </div>
  </div>
  }
</app-main-layout>
